import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Linkedin, Github, Code2 } from 'lucide-react';
import { personalInfo, contactCopy } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Message Sent!",
        description: response.data.message || "Thank you for reaching out. I'll get back to you soon.",
      });
      
      // Reset form
      setFormData({ name: '', email: '', subject: '', message: '' });
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-24 px-6 border-b border-stone-400/25 dark:border-stone-500/20 bg-white/[0.22] dark:bg-stone-950/30"
    >
      <div className="max-w-6xl mx-auto">
        <div className="mb-12">
          <h2 className="text-4xl font-bold text-stone-900 dark:text-stone-100 mb-4 leading-tight">
            {contactCopy.title}
          </h2>
          <div className="w-16 h-1 bg-amber-800 dark:bg-amber-600 rounded-full mb-4"></div>
          <p className="text-base text-stone-600 dark:text-stone-400 max-w-2xl">
            {contactCopy.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-5">
            <div className="p-6 rounded-2xl bg-white/60 dark:bg-stone-800/60 backdrop-blur-md border border-stone-200/50 dark:border-stone-700/50">
              <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-4">
                Contact Information
              </h3>
              <div className="space-y-4">
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="flex items-center space-x-3 group"
                >
                  <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/30 group-hover:bg-amber-200 dark:group-hover:bg-amber-900/50 transition-colors">
                    <Mail className="w-4 h-4 text-amber-800 dark:text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wider">
                      Email
                    </p>
                    <p className="text-sm text-stone-900 dark:text-stone-100 font-medium">
                      {personalInfo.email}
                    </p>
                  </div>
                </a>

                <a
                  href={`tel:${personalInfo.phone}`}
                  className="flex items-center space-x-3 group"
                >
                  <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/30 group-hover:bg-amber-200 dark:group-hover:bg-amber-900/50 transition-colors">
                    <Phone className="w-4 h-4 text-amber-800 dark:text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wider">
                      Phone
                    </p>
                    <p className="text-sm text-stone-900 dark:text-stone-100 font-medium">
                      {personalInfo.phone}
                    </p>
                  </div>
                </a>

                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                    <MapPin className="w-4 h-4 text-amber-800 dark:text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wider">
                      Location
                    </p>
                    <p className="text-sm text-stone-900 dark:text-stone-100 font-medium">
                      {personalInfo.location}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white/60 dark:bg-stone-800/60 backdrop-blur-md border border-stone-200/50 dark:border-stone-700/50">
              <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-4">
                Connect with Me
              </h3>
              <div className="flex space-x-3">
                <a
                  href={personalInfo.profiles.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-xl bg-stone-200/50 dark:bg-stone-800/50 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                >
                  <Linkedin className="w-5 h-5 text-stone-700 dark:text-stone-300" />
                </a>
                <a
                  href={personalInfo.profiles.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-xl bg-stone-200/50 dark:bg-stone-800/50 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                >
                  <Github className="w-5 h-5 text-stone-700 dark:text-stone-300" />
                </a>
                <a
                  href={personalInfo.profiles.leetcode}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-xl bg-stone-200/50 dark:bg-stone-800/50 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                >
                  <Code2 className="w-5 h-5 text-stone-700 dark:text-stone-300" />
                </a>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-white/60 dark:bg-stone-800/60 backdrop-blur-md border border-stone-200/50 dark:border-stone-700/50">
            <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-4">
              Send a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl bg-stone-50 dark:bg-stone-900 border-stone-200 dark:border-stone-700"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl bg-stone-50 dark:bg-stone-900 border-stone-200 dark:border-stone-700"
                />
              </div>
              <div>
                <Input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl bg-stone-50 dark:bg-stone-900 border-stone-200 dark:border-stone-700"
                />
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full rounded-xl bg-stone-50 dark:bg-stone-900 border-stone-200 dark:border-stone-700"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-stone-900 hover:bg-stone-800 dark:bg-stone-100 dark:hover:bg-stone-200 text-white dark:text-stone-900 px-6 py-5 text-sm rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
